class ComponentScroll extends Theme.Component {
  #state = "idle";
  #drag_start = 0;
  #drag_threshold = 10;
  #drag_delta = 0;
  #scroll_start = 0;
  #animation_frame_id = null;
  #snap_points = [];

  connectedCallback() {
    this.defineElements({
      track: ":scope > .scroll-track"
    });

    this.defineElementCollections({
      items: ":scope > .scroll-track > *"
    });

    super.connectedCallback();

    this.setStyles();
    this.setSnapPoints();

    requestAnimationFrame(() => {
      this.setStyles();
      this.setInitialCenter();
      this.setSnapPoints();
    });

    setTimeout(() => {
      this.setStyles();
      this.setInitialCenter();
      this.setSnapPoints();
    }, 250);

    this.onResizeHandler = Theme.Helpers.debounce(this.onResize.bind(this), 150);
    this.onScrollHandler = this.updateEdgeStates.bind(this);
    this.onPointerDownHandler = this.onPointerDown.bind(this);
    this.onPointerMoveHandler = this.onPointerMove.bind(this);
    this.onPointerUpHandler = this.onPointerUp.bind(this);
    this.onDragStartHandler = this.onDragStart.bind(this);

    window.addEventListener("resize", this.onResizeHandler);
    this.elements.track?.addEventListener("pointerdown", this.onPointerDownHandler);
    this.elements.track?.addEventListener("pointermove", this.onPointerMoveHandler);
    this.elements.track?.addEventListener("pointerup", this.onPointerUpHandler);
    this.elements.track?.addEventListener("pointercancel", this.onPointerUpHandler);
    this.elements.track?.addEventListener("dragstart", this.onDragStartHandler);
    this.elements.track?.addEventListener("scroll", this.onScrollHandler, { passive: true });
  }

  getItems() {
    return Array.from(this.querySelectorAll(":scope > .scroll-track > *"));
  }

  refresh() {
    this.setStyles();
    this.setSnapPoints();
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    window.removeEventListener("resize", this.onResizeHandler);
    this.elements.track?.removeEventListener("pointerdown", this.onPointerDownHandler);
    this.elements.track?.removeEventListener("pointermove", this.onPointerMoveHandler);
    this.elements.track?.removeEventListener("pointerup", this.onPointerUpHandler);
    this.elements.track?.removeEventListener("pointercancel", this.onPointerUpHandler);
    this.elements.track?.removeEventListener("dragstart", this.onDragStartHandler);
    this.elements.track?.removeEventListener("scroll", this.onScrollHandler);
  }

  setStyles() {
    const track = this.elements.track;
    const scrollWidth = track?.scrollWidth ?? 0;
    const clientWidth = track?.clientWidth ?? 0;
    const isScrollable = this.isScrollable();

    if (isScrollable) {
      this.classList.add("state--scrollable");
      this.updateScrollbarCompensation();
    } else {
      this.classList.remove("state--scrollable");
    }

    this.updateEdgeStates();
  }

  updateEdgeStates() {
    const track = this.elements.track;

    if (!track || !this.isScrollable()) {
      this.classList.remove("state--at-start");
      this.classList.remove("state--at-end");
      return;
    }

    const max = track.scrollWidth - track.clientWidth;
    const tolerance = 1;

    this.classList.toggle("state--at-start", track.scrollLeft <= tolerance);
    this.classList.toggle("state--at-end", track.scrollLeft >= (max - tolerance));
  }

  updateScrollbarCompensation() {
    const scrollbar_width = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbar_width > 0) {
      this.style.setProperty("--scrollbar-compensation", `${scrollbar_width / 2}px`);
    } else {
      this.style.removeProperty("--scrollbar-compensation");
    }
  }

  setSnapPoints() {
    const track = this.elements.track;

    if (!track) {
      this.#snap_points = [];
      return;
    }

    const container_box = track.getBoundingClientRect();
    const container_style = getComputedStyle(track);
    const container_padding_left = parseFloat(container_style.paddingLeft) || 0;
    const container_padding_right = parseFloat(container_style.paddingRight) || 0;
    const container_edge = container_box.left + container_padding_left;
    const container_scroll = track.scrollLeft;
    const is_centered = track.classList.contains("scroll--center");
    const inner_width = track.clientWidth - container_padding_left - container_padding_right;

    const max = track.scrollWidth - track.clientWidth;

    const points = [];

    const items = this.getItems();

    if (!items.length) {
      this.#snap_points = [];
      return;
    }

    items.forEach((element) => {
      const element_box = element.getBoundingClientRect();
      const item_scroll = element_box.left - container_edge;

      let distance = container_scroll + item_scroll;

      if (is_centered) {
        distance = distance - ((inner_width - element.clientWidth) / 2);
      }

      const clamped = Math.max(0, Math.min(distance, max));
      const rounded = Math.round(clamped);

      if (!points.includes(rounded)) {
        points.push(rounded);
      }
    });

    this.#snap_points = points;
  }

  isScrollable() {
    const track = this.elements.track;

    if (!track) {
      return false;
    }

    return (track.scrollWidth - track.clientWidth) > 1;
  }

  setInitialCenter() {
    const track = this.elements.track;

    if (!track || !track.classList.contains("scroll--center")) {
      return;
    }

    if (!this.classList.contains("state--scrollable")) {
      return;
    }

    track.scrollLeft = (track.scrollWidth - track.clientWidth) / 2;

    this.updateEdgeStates();
  }

  onResize() {
    this.setStyles();
    this.setInitialCenter();
    this.setSnapPoints();
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.#state = "tracking";
    this.#scroll_start = this.elements.track?.scrollLeft ?? 0;
    this.#drag_start = event.pageX;
  }

  onPointerMove(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    this.#drag_delta = event.pageX - this.#drag_start;

    if (this.isDrag() && this.#state === "tracking") {
      this.elements.track?.setPointerCapture(event.pointerId);
      this.#state = "dragging";
      this.classList.add("state--dragging");
      this.startAnimationLoop();
    }
  }

  onPointerUp(event) {
    if (event.pointerType === "touch") {
      this.reset();
      return;
    }

    const distance = Math.abs((this.elements.track?.scrollLeft ?? 0) - this.#scroll_start);

    if (this.#state === "dragging" && event.pointerType === "mouse") {
      this.preventDefaultClick();
    }

    if (this.#state === "dragging" || distance >= this.#drag_threshold) {
      this.animateScrollTo(this.getDirectionalSnapPoint());
    }

    this.reset();
  }

  isDrag() {
    const threshold = this.#drag_threshold;
    const distance = Math.abs(this.#drag_delta);

    return distance >= threshold;
  }

  startAnimationLoop() {
    if (this.#animation_frame_id) {
      return;
    }

    const loop = () => {
      if (this.elements.track) {
        this.elements.track.scrollLeft = this.#scroll_start - this.#drag_delta;
      }
      this.#animation_frame_id = requestAnimationFrame(loop);
    };

    this.#animation_frame_id = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    cancelAnimationFrame(this.#animation_frame_id);
    this.#animation_frame_id = null;
  }

  preventDefaultClick() {
    this.elements.track?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    }, {
      capture: true,
      once: true
    });
  }

  animateScrollTo(value) {
    if (!this.elements.track) {
      return;
    }

    this.elements.track.scrollTo({
      left: value,
      behavior: "smooth"
    });
  }

  getDirectionalSnapPoint() {
    const container_scroll = this.elements.track?.scrollLeft ?? 0;

    if (!this.#snap_points.length) {
      return container_scroll;
    }

    const direction = Math.sign(container_scroll - this.#scroll_start);

    if (direction > 0) {
      return this.getNextSnapPoint();
    }

    if (direction < 0) {
      return this.getPreviousSnapPoint();
    }

    return container_scroll;
  }

  getNextSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const next = points.find((p) => p > scroll_left);

    return next ?? points.at(-1);
  }

  getPreviousSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const previous = points.toReversed().find((p) => p < scroll_left);

    return previous ?? points[0];
  }

  nextItem() {
    this.animateScrollTo(this.getNextSnapPoint());
  }

  previousItem() {
    this.animateScrollTo(this.getPreviousSnapPoint());
  }

  nextGroup() {
    if (!this.#snap_points.length) {
      return;
    }

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    let current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    if (current_index === -1) {
      current_index = this.#snap_points.length - 1;
    }

    const index = Math.min(current_index + group_size, this.#snap_points.length - 1);

    this.animateScrollTo(this.#snap_points[index]);
  }

  previousGroup() {
    if (!this.#snap_points.length) {
      return;
    }

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    let current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    if (current_index === -1) {
      current_index = this.#snap_points.length - 1;
    }

    const index = Math.max(current_index - group_size, 0);

    this.animateScrollTo(this.#snap_points[index]);
  }

  getGroupSize() {
    const items = this.getItems();

    if (!items.length) {
      return 1;
    }

    const container_width = this.elements.track?.clientWidth ?? 0;
    const item_width = items[0].clientWidth;

    if (!item_width) {
      return 1;
    }

    return Math.max(1, Math.floor(container_width / item_width));
  }

  reset() {
    this.stopAnimationLoop();
    this.#state = "idle";
    this.#drag_start = 0;
    this.#scroll_start = 0;
    this.classList.remove("state--dragging");
  }
}

customElements.define("component-scroll", ComponentScroll);
